/**
 * New Year yard signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearYardSignsPack: ToilePack = {
    slug: 'new-year-yard-signs',
    category: 'season',
    name: 'New Year yard signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'greeting', label: 'Greeting', subject: 'A wide yard sign lettered "HAPPY NEW YEAR" large in bold with a firework border'},
        {key: 'party', label: 'Party here', subject: 'A wide yard sign lettered "THE PARTY IS HERE" with a big arrow and confetti'},
        {key: 'countdown', label: 'Countdown board', subject: 'A wide yard sign lettered "COUNTDOWN" with a large blank number panel and stars'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for a driveway stake', 'in reflective white', 'as a double-sided sign'],
    directionHint: 'try: for a driveway stake',
    seo: {
        title: 'New Year Yard Sign: Greeting, Arrow, Countdown',
        metaDescription: 'Nine new year yard sign designs — big greeting, party arrow, countdown board — bold and readable from the road, three styles.',
        h1: 'New year yard sign: greeting, arrow, countdown',
        tagline: 'Nine signs, three midnight subjects, three styles each.',
        intro: 'Bold lawn-sign artwork for the party house: a big greeting, a party-here arrow, a countdown board, each built to read from a passing car. Ask for a house-number panel, a confetti border, or your own line, and the same three styles carry it.',
        useCases: [
            "a lawn sign for a New Year's party",
            'a printed yard stake for the driveway',
            'a neighborhood countdown display',
            'a venue direction sign for guests',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine yard-sign designs — a big greeting, a party arrow, a countdown board — bold and flat, in three styles.'},
            {q: 'Will the text be readable from the road?', a: 'Yes — the lettering is built large and high-contrast for a lawn stake, not close-up reading.'},
            {q: 'What size should I print it at?', a: 'Standard yard-sign printers commonly run 18x24 inches; each design renders at print resolution and scales up cleanly.'},
            {q: 'Can I add my house number or the party start time?', a: 'The pack renders a general greeting layout; adding your own detail on top is a quick edit after downloading.'},
            {q: "Can I use this for a sign I'm printing and selling?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
